import { Product, Opportunity, PriorityScoringConfig } from '../types';

// Default scoring configuration - weights sum to 100%
export const DEFAULT_PRIORITY_SCORING_CONFIG: PriorityScoringConfig = {
  marketPotential: 20, // 20%
  gtmReadyPercent: 20, // 20%
  status: 15, // 15%
  effortLevel: 12, // 12%
  marketCrowding: 8, // 8%
  marketType: 7, // 7%
  businessModel: 5, // 5%
  supportOverhead: 5, // 5%
  features: 5, // 5%
  competitors: 2, // 2%
  age: 1 // 1%
};

export const calculatePriorityScore = (item: Product | Opportunity, config?: PriorityScoringConfig): number => {
  const scoringConfig = config || DEFAULT_PRIORITY_SCORING_CONFIG;
  let score = 0;
  
  // Market Potential
  const marketPotentialScores = {
    'Low': scoringConfig.marketPotential * 0.2,
    'Medium': scoringConfig.marketPotential * 0.6,
    'High': scoringConfig.marketPotential
  };
  score += marketPotentialScores[item.marketPotential || 'Low'] || 0;
  
  // GTM Ready Percentage
  // Higher GTM readiness = higher priority (closer to launch)
  score += Math.round(((item.gtmReadyPercent || 0) / 100) * scoringConfig.gtmReadyPercent);
  
  // Status-based scoring - different for products vs opportunities
  if ('features' in item) {
    // Product status scoring
    const productStatusScores = {
      'Ideas': scoringConfig.status * 0.25,
      'Research': scoringConfig.status * 0.5,
      'Development': scoringConfig.status * 0.75,
      'Testing': scoringConfig.status * 0.9,
      'Launch': scoringConfig.status
    };
    score += productStatusScores[item.status || 'Ideas'] || 0;
  } else {
    // Opportunity status scoring
    const opportunityStatusScores = {
      'Prospecting': scoringConfig.status * 0.2,
      'Qualified': scoringConfig.status * 0.4,
      'Proposal': scoringConfig.status * 0.6,
      'Negotiation': scoringConfig.status * 0.8,
      'Closed Won': scoringConfig.status,
      'Closed Lost': scoringConfig.status * 0.1
    };
    score += opportunityStatusScores[item.status || 'Prospecting'] || 0;
  }
  
  // Effort Level
  // Lower effort = higher priority (easier wins)
  const effortScores = {
    'Low': scoringConfig.effortLevel,
    'Medium': scoringConfig.effortLevel * 0.67,
    'High': scoringConfig.effortLevel * 0.33
  };
  score += effortScores[item.effortLevel || 'Medium'] || 0;
  
  // Feature completeness (for products) or Win Probability (for opportunities)
  if ('features' in item) {
    // Product: More features = higher priority (more developed)
    const featureScore = Math.min((item.features?.length || 0) * (scoringConfig.features / 5), scoringConfig.features);
    score += featureScore;
  } else {
    // Opportunity: Higher win probability = higher priority
    const winProbabilityScore = Math.round(((item.winProbability || 0) / 100) * scoringConfig.features);
    score += winProbabilityScore;
  }
  
  // Market Crowding
  // Less competition = higher priority (easier to stand out)
  const marketCrowdingScores = {
    'No Competition': scoringConfig.marketCrowding,
    'Low Competition': scoringConfig.marketCrowding * 0.75,
    'Medium Competition': scoringConfig.marketCrowding * 0.5,
    'High Competition': scoringConfig.marketCrowding * 0.25
  };
  score += marketCrowdingScores[item.marketCrowding || 'Medium Competition'] || scoringConfig.marketCrowding * 0.5;
  
  // Market Type
  // Blue Ocean = higher priority (untapped market)
  const marketTypeScores = {
    'Blue Ocean': scoringConfig.marketType,
    'Red Ocean': scoringConfig.marketType * 0.43
  };
  score += marketTypeScores[item.marketType || 'Red Ocean'] || scoringConfig.marketType * 0.43;
  
  // Business Model
  // SaaS and scalable models get higher priority
  const businessModelScores = {
    'SaaS': scoringConfig.businessModel,
    'API/Platform': scoringConfig.businessModel,
    'Marketplace': scoringConfig.businessModel * 0.8,
    'Mobile App': scoringConfig.businessModel * 0.8,
    'E-commerce': scoringConfig.businessModel * 0.6,
    'Hardware': scoringConfig.businessModel * 0.4,
    'Consulting': scoringConfig.businessModel * 0.2,
    'Other': scoringConfig.businessModel * 0.4
  };
  score += businessModelScores[item.businessModel || 'Other'] || scoringConfig.businessModel * 0.4;
  
  // Support Overhead
  // Lower support overhead = higher priority (easier to scale)
  const supportOverheadScores = {
    'Low': scoringConfig.supportOverhead,
    'Medium': scoringConfig.supportOverhead * 0.6,
    'High': scoringConfig.supportOverhead * 0.2
  };
  score += supportOverheadScores[item.supportOverhead || 'Medium'] || scoringConfig.supportOverhead * 0.6;
  
  // Competitor awareness
  // Having competitors identified = higher priority (market research done)
  const competitorScore = (item.competitors && item.competitors.length > 0) ? scoringConfig.competitors : 0;
  score += competitorScore;
  
  // Age factor
  // Older items get slight priority boost (been around longer)
  if (item.createdAt) {
    const daysSinceCreation = Math.floor((Date.now() - item.createdAt.getTime()) / (1000 * 60 * 60 * 24));
    const ageScore = Math.min(daysSinceCreation / 30, scoringConfig.age); // Max points for 30+ days
    score += ageScore;
  }
  
  // Ensure score is between 0-100
  return Math.max(0, Math.min(100, Math.round(score)));
};

export const getPriorityColor = (score: number): string => {
  if (score >= 80) return 'bg-red-500 text-white';
  if (score >= 60) return 'bg-orange-500 text-white';
  if (score >= 40) return 'bg-yellow-500 text-gray-800';
  if (score >= 20) return 'bg-blue-500 text-white';
  return 'bg-gray-500 text-white';
};

export const getPriorityLabel = (score: number): string => {
  if (score >= 80) return 'Critical';
  if (score >= 60) return 'High';
  if (score >= 40) return 'Medium';
  if (score >= 20) return 'Low';
  return 'Minimal';
};
